import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Page, StatusPill, Field } from "../components/Primitives";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { motion } from "framer-motion";

const STAGES = [
  { key: "enquiry", label: "Enquiry" },
  { key: "test_drive", label: "Test Drive" },
  { key: "negotiation", label: "Negotiation" },
  { key: "booked", label: "Booked" },
  { key: "delivered", label: "Delivered" },
  { key: "lost", label: "Lost" },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [cars, setCars] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", interested_car_id: "", source: "walk-in",
    stage: "enquiry", notes: "", budget: 0, finance_required: false,
    trade_in_car: "", trade_in_value: 0,
  });

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/leads"), api.get("/showroom/cars")]);
    setLeads(l.data); setCars(c.data);
  };
  useEffect(() => { load(); }, []);

  const byStage = (s) => leads.filter((l) => l.stage === s);

  const move = async (id, stage) => {
    await api.patch(`/leads/${id}/stage`, null, { params: { stage } });
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, budget: +form.budget, trade_in_value: +form.trade_in_value });
      toast.success("Lead added");
      setOpen(false); load();
    } catch (err) { toast.error("Failed"); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page
      number="03"
      title="Leads & CRM"
      subtitle="Drag through the funnel — enquiry to delivery."
      right={<button className="kk-btn-primary" onClick={() => setOpen(true)} data-testid="add-lead"><Plus size={14} /> New Lead</button>}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => (
          <div key={s.key} className="kk-kanban-col flex-shrink-0 w-72" data-testid={`col-${s.key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="kk-overline">{s.label}</span>
              <span className="kk-pill kk-pill-black">{byStage(s.key).length}</span>
            </div>
            <div className="space-y-3">
              {byStage(s.key).map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className="kk-card p-4"
                >
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-xs text-[var(--kk-muted)]">{l.phone}</div>
                  {l.interested_car_id && (
                    <div className="text-xs mt-1 font-mono">
                      {(cars.find((c) => c.id === l.interested_car_id) || {}).model || "—"}
                    </div>
                  )}
                  {l.budget > 0 && <div className="text-xs mt-1">Budget: ₹{(+l.budget).toLocaleString("en-IN")}</div>}
                  <div className="mt-3 flex flex-wrap gap-1">
                    {STAGES.filter((x) => x.key !== s.key).map((x) => (
                      <button key={x.key} onClick={() => move(l.id, x.key)} className="text-[0.6rem] uppercase tracking-wider border border-[var(--kk-border)] px-1.5 py-0.5 hover:bg-[var(--kk-black)] hover:text-white" data-testid={`move-${l.id}-${x.key}`}>
                        → {x.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ))}
              {byStage(s.key).length === 0 && (
                <div className="text-xs text-[var(--kk-muted)] text-center py-6">—</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[var(--kk-surface)] p-8 overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl uppercase">New Lead</h2>
              <button type="button" onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-5">
              <Field label="Name"><input className="kk-input" required value={form.name} onChange={upd("name")} data-testid="lead-name" /></Field>
              <Field label="Phone"><input className="kk-input" required value={form.phone} onChange={upd("phone")} data-testid="lead-phone" /></Field>
              <Field label="Email"><input className="kk-input" type="email" value={form.email} onChange={upd("email")} /></Field>
              <Field label="Source">
                <select className="kk-input" value={form.source} onChange={upd("source")}>
                  <option value="walk-in">Walk-in</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social">Social</option>
                  <option value="call">Call</option>
                </select>
              </Field>
              <Field label="Interested car">
                <select className="kk-input" value={form.interested_car_id} onChange={upd("interested_car_id")}>
                  <option value="">—</option>
                  {cars.map((c) => <option key={c.id} value={c.id}>{`${c.make} ${c.model} · ${c.year}`}</option>)}
                </select>
              </Field>
              <Field label="Stage">
                <select className="kk-input" value={form.stage} onChange={upd("stage")}>
                  {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Budget (₹)"><input className="kk-input" type="number" value={form.budget} onChange={upd("budget")} /></Field>
              <Field label="Trade-in value (₹)"><input className="kk-input" type="number" value={form.trade_in_value} onChange={upd("trade_in_value")} /></Field>
              <Field label="Trade-in car"><input className="kk-input" value={form.trade_in_car} onChange={upd("trade_in_car")} /></Field>
              <Field label="Finance required">
                <select className="kk-input" value={form.finance_required ? "1" : "0"} onChange={(e) => setForm({ ...form, finance_required: e.target.value === "1" })}>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </Field>
              <div className="col-span-2"><Field label="Notes"><textarea className="kk-input min-h-[70px]" value={form.notes} onChange={upd("notes")} /></Field></div>
            </div>
            <button className="kk-btn-primary mt-6" type="submit" data-testid="save-lead">Save Lead</button>
          </form>
        </div>
      )}
    </Page>
  );
}
